import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Mail, Lock, Globe, ArrowRight, Loader2 } from 'lucide-react'
import AuthLayout from '@/components/AuthLayout'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface LoginProps {
  showSubdomain: boolean
  recaptchaSiteKey: string
  loginUrl: string
  forgotPasswordUrl: string
  locale: string
  logoUrl: string
  translations: {
    welcome: string
    pleaseLogin: string
    login: string
    subdomain: string
    email: string
    password: string
    rememberMe: string
    forgotPassword: string
  }
}

/* ------------------------------------------------------------------ */
/*  ReCaptcha helpers (v2 checkbox – loaded dynamically)               */
/* ------------------------------------------------------------------ */

declare global {
  interface Window {
    grecaptcha?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => number
      getResponse: (widgetId?: number) => string
      reset: (widgetId?: number) => void
    }
    onRecaptchaLoad?: () => void
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Login({
  showSubdomain,
  recaptchaSiteKey,
  loginUrl,
  forgotPasswordUrl,
  locale,
  logoUrl,
  translations: t,
}: LoginProps) {
  /* ---- form state ---- */
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [subdomain, setSubdomain] = useState('')
  const [processing, setProcessing] = useState(false)

  /* ---- recaptcha ---- */
  const recaptchaRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<number | null>(null)
  const [recaptchaToken, setRecaptchaToken] = useState('')

  const renderWidget = useCallback(() => {
    if (recaptchaRef.current && window.grecaptcha && widgetIdRef.current === null) {
      widgetIdRef.current = window.grecaptcha.render(recaptchaRef.current, {
        sitekey: recaptchaSiteKey,
        callback: (token: string) => setRecaptchaToken(token),
        'expired-callback': () => setRecaptchaToken(''),
      })
    }
  }, [recaptchaSiteKey])

  useEffect(() => {
    if (window.grecaptcha) {
      renderWidget()
      return
    }
    window.onRecaptchaLoad = renderWidget
    const script = document.createElement('script')
    script.src =
      'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit'
    script.async = true
    script.defer = true
    document.head.appendChild(script)
    return () => {
      window.onRecaptchaLoad = undefined
    }
  }, [renderWidget])

  /* ---- submit ---- */
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setProcessing(true)

    router.post(
      loginUrl,
      {
        user: {
          email,
          password,
          remember_me: rememberMe ? '1' : '0',
        },
        ...(showSubdomain ? { subdomain } : {}),
        'g-recaptcha-response': recaptchaToken,
      },
      {
        onFinish: () => {
          setProcessing(false)
          if (window.grecaptcha && widgetIdRef.current !== null) {
            window.grecaptcha.reset(widgetIdRef.current)
            setRecaptchaToken('')
          }
        },
      },
    )
  }

  /* ---- render ---- */
  return (
    <>
      <Head title={t.login} />

      <AuthLayout
        locale={locale}
        logoUrl={logoUrl}
        brandingTitle={t.welcome}
        brandingSubtitle={t.pleaseLogin}
      >
        {/* Card */}
        <div className="rounded-2xl bg-white shadow-xl shadow-slate-200/60 border border-slate-100 p-8 sm:p-10">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-[#252854] mb-1">{t.login}</h1>
            <p className="text-sm text-slate-400">{t.pleaseLogin}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Subdomain */}
            {showSubdomain && (
              <div className="space-y-1.5">
                <Label htmlFor="subdomain" className="text-xs font-medium text-slate-500">
                  {t.subdomain}
                </Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  <Input
                    id="subdomain"
                    value={subdomain}
                    onChange={(e) => setSubdomain(e.target.value)}
                    className="pl-10 h-11 rounded-xl border-slate-200 bg-slate-50/50 focus:bg-white transition-colors"
                    autoFocus
                    autoComplete="organization"
                  />
                </div>
              </div>
            )}

            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-medium text-slate-500">
                {t.email}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 rounded-xl border-slate-200 bg-slate-50/50 focus:bg-white transition-colors"
                  autoComplete="email"
                  autoFocus={!showSubdomain}
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-medium text-slate-500">
                {t.password}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-11 rounded-xl border-slate-200 bg-slate-50/50 focus:bg-white transition-colors"
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>

            {/* Remember + Forgot */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember_me"
                  checked={rememberMe}
                  onCheckedChange={(checked: boolean) => setRememberMe(checked)}
                  className="data-[state=checked]:bg-[#2DD4BF] data-[state=checked]:border-[#2DD4BF]"
                />
                <Label htmlFor="remember_me" className="cursor-pointer font-normal text-sm text-slate-500">
                  {t.rememberMe}
                </Label>
              </div>
              <a
                href={forgotPasswordUrl}
                className="text-sm font-medium text-[#8B5CF6] hover:text-[#7C3AED] transition-colors"
              >
                {t.forgotPassword}
              </a>
            </div>

            {/* reCAPTCHA */}
            <div className="flex justify-center">
              <div ref={recaptchaRef} />
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={processing}
              className="w-full h-11 rounded-xl text-sm font-medium bg-gradient-to-r from-[#2DD4BF] to-[#8B5CF6] hover:from-[#14B8A6] hover:to-[#7C3AED] text-white shadow-lg shadow-[#8B5CF6]/20 transition-all duration-200"
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span className="flex items-center gap-2">
                  {t.login}
                  <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </Button>
          </form>
        </div>
      </AuthLayout>
    </>
  )
}
